import type { LimitPayerType } from "@/shared/services/payments/paymentCorridors.service";

/**
 * Brazilian tax documents: the CPF (a person, 11 digits) and the CNPJ (a company, 14 digits). Both end in two
 * check digits, so a typo is caught on screen instead of reaching the provider as a refused payment.
 */
export type BrazilDocumentType = "CPF" | "CNPJ";

const digitsOf = (raw: string): string => raw.replace(/\D/g, "");

/** A CPF with valid check digits. The eleven repeated digits pass the arithmetic and are refused anyway. */
export function isValidCpf(raw: string): boolean {
  const d = digitsOf(raw);
  if (d.length !== 11 || /^(\d)\1{10}$/.test(d)) return false;
  const check = (length: number): number => {
    let sum = 0;
    for (let i = 0; i < length; i++) sum += Number(d[i]) * (length + 1 - i);
    const rest = (sum * 10) % 11;
    return rest === 10 ? 0 : rest;
  };
  return check(9) === Number(d[9]) && check(10) === Number(d[10]);
}

const CNPJ_WEIGHTS_12 = [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
const CNPJ_WEIGHTS_13 = [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];

/** A CNPJ with valid check digits. */
export function isValidCnpj(raw: string): boolean {
  const d = digitsOf(raw);
  if (d.length !== 14 || /^(\d)\1{13}$/.test(d)) return false;
  const check = (weights: number[]): number => {
    const sum = weights.reduce((acc, weight, i) => acc + Number(d[i]) * weight, 0);
    const rest = sum % 11;
    return rest < 2 ? 0 : 11 - rest;
  };
  return check(CNPJ_WEIGHTS_12) === Number(d[12]) && check(CNPJ_WEIGHTS_13) === Number(d[13]);
}

/** Which document the digits are, by length alone — null while it is neither yet. */
export function brazilDocumentType(raw: string): BrazilDocumentType | null {
  const length = digitsOf(raw).length;
  return length === 11 ? "CPF" : length === 14 ? "CNPJ" : null;
}

export function isValidBrazilDocument(raw: string): boolean {
  const type = brazilDocumentType(raw);
  return type === "CPF" ? isValidCpf(raw) : type === "CNPJ" ? isValidCnpj(raw) : false;
}

/** The payer kind the corridor limits are checked against: a CNPJ is a company, a CPF a person. */
export function brazilPayerType(raw: string): LimitPayerType | null {
  const type = brazilDocumentType(raw);
  return type === "CNPJ" ? "COMPANY" : type === "CPF" ? "PERSON" : null;
}

/** Progressive mask while typing: CPF up to 11 digits, CNPJ from the 12th. */
export function formatBrazilDocument(raw: string): string {
  const d = digitsOf(raw).slice(0, 14);
  if (d.length <= 11) {
    if (d.length > 9) return `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6, 9)}-${d.slice(9)}`;
    if (d.length > 6) return `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6)}`;
    if (d.length > 3) return `${d.slice(0, 3)}.${d.slice(3)}`;
    return d;
  }
  if (d.length > 12)
    return `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5, 8)}/${d.slice(8, 12)}-${d.slice(12)}`;
  return `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5, 8)}/${d.slice(8)}`;
}
